// The Street — M2 public-company layer (ARCHITECTURE_M2.md §6). Design §3
// Stage 5 / §10: price = fundamentals (the bigger of the P/S sales story and
// the P/E earnings story) × sentiment × guidance credibility, reverting monthly
// with noise; the quarterly earnings ritual is the beat-and-raise treadmill
// (guidance policy prices in growth now and collects the bill later);
// delisting is six months under a dollar.

import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { interpolate } from "../calendar.js";
import { clamp } from "../prng.js";
import { refreshFounderShare, totalShares, ttmEarnings } from "./finance.js";

export function processStreet(ctx: TickContext): void {
  const { s, content } = ctx;
  const street = s.street;
  if (!street || s.dead) return;
  const cfg = content.economy.street;
  const m = s.monthIndex;
  const fin = s.finance;

  // ---- monthly trading: revert toward fair value, then noise ----
  const ttm = fin.ttmRevenue.reduce((a, b) => a + b, 0);
  const sentF = cfg.sentimentBase + cfg.sentimentSlope * interpolate(content.economy.sentiment, m);
  const credF = cfg.credBase + cfg.credSlope * street.credibility;
  // guided growth is priced in immediately — that's why stretching is tempting
  const guideF =
    street.guidedQuarterRevenue !== null && street.lastQuarterRevenue > 0
      ? clamp(
          1 + cfg.guideFairValueCoeff * (street.guidedQuarterRevenue / street.lastQuarterRevenue - 1),
          cfg.guideGrowthClampLo,
          cfg.guideGrowthClampHi,
        )
      : 1;
  // whichever story is bigger sets the price: sales (pre-profit) or earnings —
  // a fat-margin company is a P/E stock, not a P/S stock
  const story = Math.max(
    interpolate(cfg.psMultiple, m) * ttm,
    interpolate(cfg.peMultiple, m) * Math.max(0, ttmEarnings(s.kpiHistory)),
  );
  const fairEquity = story * sentF * credF * guideF + Math.max(0, s.cash);
  const total = totalShares(fin);
  const fairPrice = fairEquity / total;
  street.sharePrice += (fairPrice - street.sharePrice) * cfg.priceReversion * ctx.monthsPerTick;
  const sigma = cfg.priceSigma;
  street.sharePrice *= ctx.rng.logNormal(-(sigma * sigma) / 2, sigma);
  street.sharePrice = Math.max(0.01, street.sharePrice);

  // ---- quarterly earnings vs. the number you gave them ----
  if (m % 3 === 2) {
    const prior = s.kpiHistory.slice(-2); // two completed months of this quarter
    const qRev = prior.reduce((a, k) => a + k.revenue - k.nreRevenue, 0) + ctx.revenue;
    let result = "first-report";
    if (street.guidedQuarterRevenue !== null) {
      const ratio = qRev / Math.max(1, street.guidedQuarterRevenue);
      if (ratio >= cfg.beatThreshold) {
        result = "beat";
        street.credibility = clamp(street.credibility + cfg.beatCredDelta, 0.05, 1);
        street.sharePrice *= 1 + cfg.beatPricePop;
        street.consecutiveMisses = 0;
      } else if (ratio < cfg.missThreshold) {
        result = "miss";
        street.credibility = clamp(street.credibility - cfg.missCredDelta, 0.05, 1);
        street.sharePrice *= 1 - cfg.missPricePenalty;
        street.consecutiveMisses += 1;
      } else {
        result = "inline";
        street.credibility = clamp(street.credibility + cfg.inlineCredDelta, 0.05, 1);
        street.consecutiveMisses = 0;
      }
    }
    // set next quarter's guide from the internal forecast, shaded by policy.
    // The forecast extrapolates only part of the observed q/q rate (analysts
    // model deceleration) — a +30% quarter forecasts +15%, not +30% again,
    // so a fast grower isn't guaranteed to miss the moment growth cools.
    const growth =
      street.lastQuarterRevenue > 0 ? clamp(qRev / street.lastQuarterRevenue, cfg.growthClampLo, cfg.growthClampHi) : 1.1;
    const forecastGrowth = 1 + cfg.guideGrowthDecay * (growth - 1);
    const guide = Math.round(qRev * forecastGrowth * (1 + street.guidancePolicy * cfg.guidePolicySpan));
    const guided = street.guidedQuarterRevenue;
    street.guidedQuarterRevenue = guide;
    street.lastQuarterRevenue = qRev;
    emit(
      ctx,
      "earnings",
      result === "first-report"
        ? `First earnings call as a public company: $${(qRev / 1e6).toFixed(1)}M. Guidance set at $${(guide / 1e6).toFixed(1)}M.`
        : result === "beat"
          ? `Earnings beat: $${(qRev / 1e6).toFixed(1)}M vs $${((guided ?? 0) / 1e6).toFixed(1)}M guided. The street applauds.`
          : result === "miss"
            ? `Earnings MISS: $${(qRev / 1e6).toFixed(1)}M vs $${((guided ?? 0) / 1e6).toFixed(1)}M guided. The stock gets punished.`
            : `Earnings in line: $${(qRev / 1e6).toFixed(1)}M.`,
      { result, quarterRevenue: qRev, guided: guided ?? 0, nextGuide: guide, credibility: street.credibility },
    );
  }

  // ---- delisting clock ----
  if (street.sharePrice < cfg.delistPrice) {
    street.belowListingMonths += ctx.monthsPerTick;
    if (street.belowListingMonths === cfg.delistWarnMonths) {
      emit(
        ctx,
        "delistWarning",
        `Listing-compliance notice: ${cfg.delistMonths - street.belowListingMonths} months under $${cfg.delistPrice.toFixed(2)} before delisting.`,
        { price: street.sharePrice, monthsBelow: street.belowListingMonths },
      );
    }
    if (street.belowListingMonths >= cfg.delistMonths) {
      s.dead = true;
      s.deathMonth = m;
      s.deathReason = "delisted";
      emit(ctx, "delisted", "Delisted. The pink sheets are where chip companies go to disappear.", {
        price: street.sharePrice,
      });
    }
  } else {
    street.belowListingMonths = 0;
  }

  street.priceHistory.push({ m, price: street.sharePrice, cap: street.sharePrice * total });
  while (street.priceHistory.length > 260) street.priceHistory.shift();
}

/** Sell new shares into the market (post-IPO financing). Returns a rejection reason or null. */
export function secondaryOffering(ctx: TickContext, amount: number): string | null {
  const { s, content } = ctx;
  const cfg = content.economy.street;
  const street = s.street;
  if (s.stage !== "public" || !street) return "secondary offerings require a public listing";
  if (s.finance.secondaryCooldownMonths > 0) return "the market needs a breather after the last offering";
  const window = interpolate(content.economy.ipo.window, s.monthIndex);
  if (window < cfg.secondaryWindowFloor) return "the window is shut — no appetite for new paper";
  const maxAmount = Math.round(street.sharePrice * totalShares(s.finance) * cfg.secondaryMaxCapFrac);
  if (amount > maxAmount) {
    return `the market will only absorb ${Math.round(cfg.secondaryMaxCapFrac * 100)}% of market cap per offering — $${(maxAmount / 1e6).toFixed(0)}M at today's price`;
  }
  const priceNet = street.sharePrice * (1 - cfg.secondaryDiscount);
  const shares = Math.round(amount / priceNet);
  if (shares <= 0) return "amount too small to price an offering";
  s.finance.publicShares += shares;
  refreshFounderShare(ctx);
  const net = Math.round(amount * (1 - cfg.secondaryFee));
  charge(ctx, "secondary", net, `secondary offering: ${shares.toLocaleString()} shares at $${priceNet.toFixed(2)} net of fees`);
  ctx.financingInflow += net;
  s.finance.secondaryCooldownMonths = cfg.secondaryCooldownMonths;
  street.sharePrice *= 1 - cfg.secondaryDiscount / 2; // supply is not free
  emit(ctx, "secondaryCompleted", `Secondary offering raised $${(net / 1e6).toFixed(1)}M (founder share now ${(s.finance.founderShare * 100).toFixed(1)}%).`, {
    net,
    shares,
    founderShare: s.finance.founderShare,
  });
  return null;
}

/** Retire float with cash. Returns a rejection reason or null. */
export function buyback(ctx: TickContext, amount: number): string | null {
  const { s, content } = ctx;
  const cfg = content.economy.street;
  const street = s.street;
  if (s.stage !== "public" || !street) return "buybacks require a public listing";
  if (amount > s.cash) return "not enough cash";
  const price = street.sharePrice * (1 + cfg.buybackPremium);
  const shares = Math.min(Math.round(amount / price), Math.floor(s.finance.publicShares * 0.5));
  if (shares <= 0) return "not enough float to buy back at that size";
  const spent = Math.round(shares * price);
  s.finance.publicShares -= shares;
  refreshFounderShare(ctx);
  charge(ctx, "buyback", -spent, `buyback: ${shares.toLocaleString()} shares at $${price.toFixed(2)}`);
  street.sharePrice *= 1 + cfg.buybackPremium / 2; // demand is not free either
  emit(ctx, "buybackCompleted", `Bought back ${shares.toLocaleString()} shares for $${(spent / 1e6).toFixed(1)}M.`, {
    spent,
    shares,
    founderShare: s.finance.founderShare,
  });
  return null;
}
